import {h, Fragment} from "preact"
import { useFacet, useActions, useAppStateSelector } from "@nosto/preact"
import { useEffect, useRef, useState, useMemo, useCallback } from "preact/hooks";
import Checkbox from "./elements/Checkbox"

import { facetConfigs } from "../config"
import { 
    fetchFullFacet, 
    mapFacetHierarchy, 
    getBranchValues,
    getValuesOutsideBranch,
    areAllSiblingsSelected,
    getExistingSiblingValues,
    getExistingChildValues,
    findParentOfItem,
    consolidateParentsRecursively,
    preserveSiblingBranchesAtAllLevels,
    handleParentDeselection
} from "../helpers"

export default function NestedCategories({ fullNestedFacet, splitNestedFacet, activeFacet, handleFacetToggle }) {
    const { active, toggleActive, toggleProductFilter } = useFacet(splitNestedFacet);
    const [ fullFacetData, setFullFacetData ] = useState(null);
    // const [ dropdownHeight, setDropdownHeight ] = useState(0);
    // const dropdownRef = useRef(null);
    const fullFacetConfigs = useMemo(() => facetConfigs[fullNestedFacet.id], [ fullNestedFacet ]);

    const desktopActive = activeFacet === splitNestedFacet.id;
    const isActive = window.innerWidth >= 1240 ? desktopActive : active;

    const mappedData = useMemo(() => {
        if (!fullFacetData || !fullFacetConfigs?.separator) return [];
        return mapFacetHierarchy(fullFacetData, fullFacetConfigs.separator);
    }, [ fullFacetData, fullFacetConfigs ]);

    useEffect(() => {
        const loadFullFacetData = async () => {
            try {
                const data = await fetchFullFacet(fullNestedFacet.id);
                setFullFacetData(data);
            } catch (error) {
                console.warn("[Nosto serp] Failed to get nested facet data:", error);
            }
        };
        
        if (!fullFacetData && fullNestedFacet?.id) {
            loadFullFacetData();
        }
    }, []);

    // useEffect(() => {
    //     if(dropdownRef?.current) {
    //         requestAnimationFrame(() => {
    //             if(dropdownRef?.current) {
    //                 const element = dropdownRef.current;
    //                 const originalMaxHeight = element.style.maxHeight;
    //                 const originalHeight = element.style.height;
                    
    //                 element.style.maxHeight = 'none';
    //                 element.style.height = 'auto';

    //                 const naturalHeight = element.scrollHeight;
    //                 element.style.maxHeight = originalMaxHeight;
    //                 element.style.height = originalHeight;
    //                 setDropdownHeight(naturalHeight);
    //             }
    //         });
    //     }
    // }, [ dropdownRef?.current, mappedData, splitNestedFacet?.data ]);

    const handleClick = useCallback(() => {
        if(window.innerWidth >= 1240) {
            handleFacetToggle(splitNestedFacet.id);
        } else {
            toggleActive();
        }
    }, [handleFacetToggle, splitNestedFacet.id, toggleActive]);

    return (
        <li class="ns-facet ns-nested-facet">
            <button
                type="button"
                class="ns-facet-toggle ns-w-100 ns-flex ns-flex-nowrap ns-items-center ns-justify-between"
                data-nosto-element="facet"
                onClick={handleClick}
                aria-controls={`ns-${splitNestedFacet.id}-sub-menu`}
                aria-expanded={isActive}
                aria-label={`${isActive ? 'Collapse' : 'Expand' } ${fullFacetConfigs.name}`}
            >
                <h3 id={`ns-${splitNestedFacet.id}-title`}>
                    {fullFacetConfigs.name}
                </h3>
                <span 
                    aria-hidden="true" 
                    role="presentation" 
                    class="ns-plus-minus-icon ns-relative"
                />
            </button>
            <div
                class="ns-facet-body"
                id={`ns-${splitNestedFacet.id}-sub-menu`}
                aria-labelledby={`ns-${splitNestedFacet.id}-title`}
                aria-hidden={!isActive}
                // style={`--dropdown-height: ${dropdownHeight}px;`} // css transition
                // ref={dropdownRef}
            >
                <ul class="ns-filter-list ns-list-unstyled ns-flex ns-flex-col" role="menu">
                    {mappedData.map((item) => {
                        const filter = splitNestedFacet.data.find(dataItem => dataItem.value === item.value);
                        return (item.children && filter ? (
                            <CheckboxWithChildren 
                                key={item.value}
                                item={item}
                                filter={filter}
                                splitNestedFacet={splitNestedFacet}
                                parentFilters={[]}
                                mappedData={mappedData}
                            />
                        ) : filter ? (
                            <Checkbox
                                key={item.value}
                                valueName={facetConfigs[splitNestedFacet.id]?.nameMap?.[item.value] || item.value}
                                value={filter.value.replace("Category: ", "")}
                                selected={filter.selected}
                                id={`ns-${splitNestedFacet.id}-${filter.value.toString().toLowerCase().replace(/\s+/g, '-')}`}
                                onChange={e => {
                                    e.preventDefault()
                                    toggleProductFilter(splitNestedFacet.field, filter.value, !filter.selected)
                                }}
                                className=""
                            />
                        ) : null);
                    })}
                </ul>
            </div>
        </li>
    )
}

function CheckboxWithChildren({ item, filter, splitNestedFacet, parentFilters = [], mappedData }) {
    const { curFilters } = useAppStateSelector(state => ({ curFilters: state.query?.products?.filter || [] }));
    const { updateSearch } = useActions();
    const [ open, setOpen ] = useState(false);
    // const [ dropdownHeight, setDropdownHeight ] = useState(0);
    // const dropdownRef = useRef(null);

    // useEffect(() => {
    //     if(dropdownRef?.current) {
    //         requestAnimationFrame(() => {
    //             if(dropdownRef?.current) {
    //                 const element = dropdownRef.current;
    //                 const originalMaxHeight = element.style.maxHeight;
    //                 const originalHeight = element.style.height;
                    
    //                 element.style.maxHeight = 'none';
    //                 element.style.height = 'auto';

    //                 const naturalHeight = element.scrollHeight;
    //                 element.style.maxHeight = originalMaxHeight;
    //                 element.style.height = originalHeight;
    //                 setDropdownHeight(naturalHeight);
    //             }
    //         });
    //     }
    // }, [ dropdownRef?.current, item.children ]);

    const handleParentFilterChange = useCallback((e) => {
        e.preventDefault();
        const otherFiltersInQuery = curFilters.filter(f => f.field !== splitNestedFacet.field);
        const currentFilterInQuery = curFilters.find(f => f.field === splitNestedFacet.field);
        const currentValues = currentFilterInQuery?.value || [];
        
        const branchValues = getBranchValues(item);
        const valuesOutsideBranch = getValuesOutsideBranch(currentValues, branchValues);
        const anyParentSelected = parentFilters.some(parentFilter => parentFilter?.selected);

        // Preserve sibling branch values at all levels using helper function
        const siblingBranchValues = preserveSiblingBranchesAtAllLevels(item, mappedData, currentValues);

        let updatedValues = [...valuesOutsideBranch, ...siblingBranchValues];
        let parentOfCurrent = null;
        for (const rootItem of mappedData) {
            parentOfCurrent = findParentOfItem(rootItem, item.value);
            if (parentOfCurrent) break;
        }
        
        if (parentOfCurrent && parentOfCurrent.children) {
            const existingSiblings = getExistingSiblingValues(parentOfCurrent, item.value, splitNestedFacet.data)
                .filter(siblingValue => currentValues.includes(siblingValue));
            updatedValues.push(...existingSiblings);
        }

        if (anyParentSelected) {
            // Case: Any ancestor is selected - use helper function for deselection logic
            const deselectionValues = handleParentDeselection(item, parentFilters, mappedData, splitNestedFacet.data);
            
            // Remove selected ancestors that are being expanded
            const selectedAncestors = parentFilters
                .filter(pf => pf?.selected)
                .map(pf => pf.value);
            updatedValues = updatedValues.filter(value => !selectedAncestors.includes(value));
            updatedValues.push(...deselectionValues);
        } else {
            // Case: No parent selected in hierarchy  
            if (filter.selected) {
                // Toggling OFF: Remove this value (siblings already preserved above)
            } else {
                // Toggling ON: Add this value (siblings already preserved above)
                updatedValues.push(filter.value);
                
                // Check if all siblings are now selected - if so, consolidate to parent
                if (parentOfCurrent && parentOfCurrent.children) {
                    const allSiblingValues = getExistingSiblingValues(parentOfCurrent, '', splitNestedFacet.data);
                    allSiblingValues.push(item.value);
                    
                    const allSiblingsSelected = allSiblingValues.every(siblingValue => 
                        updatedValues.includes(siblingValue)
                    );
                    
                    if (allSiblingsSelected) {
                        updatedValues = updatedValues.filter(v => !allSiblingValues.includes(v));
                        if (parentOfCurrent.value) {
                            updatedValues.push(parentOfCurrent.value);
                            try {
                                updatedValues = consolidateParentsRecursively(parentOfCurrent, updatedValues, mappedData, splitNestedFacet.data);
                            } catch (e) {
                                // Fallback to original values if consolidation fails
                                updatedValues = [...valuesOutsideBranch, ...siblingBranchValues, filter.value];
                            }
                        }
                    }
                }
                setOpen(true);
            }
        }
        
        const allFilters = [...otherFiltersInQuery];
        if (updatedValues.length > 0) {
            allFilters.push({ field: splitNestedFacet.field, value: updatedValues });
        }
        updateSearch({ products: { filter: allFilters } });
    }, [curFilters, item, splitNestedFacet, parentFilters, mappedData, filter, updateSearch]);

    const handleChildFilterChange = useCallback((childFilter, parentItem) => {
        const otherFiltersInQuery = curFilters.filter(f => f.field !== splitNestedFacet.field);
        const currentFilterInQuery = curFilters.find(f => f.field === splitNestedFacet.field);
        const currentValues = currentFilterInQuery?.value || [];
        
        const branchValues = getBranchValues(parentItem);
        const valuesOutsideBranch = getValuesOutsideBranch(currentValues, branchValues);
        
        // Find siblings of the parentItem (at the same level) and preserve any that are selected
        let parentItemSiblings = [];
        
        // Find the parent of parentItem to get its siblings
        for (const rootItem of mappedData) {
            const grandParent = findParentOfItem(rootItem, parentItem.value);
            if (grandParent) {
                parentItemSiblings = getExistingSiblingValues(grandParent, parentItem.value, splitNestedFacet.data);
                break;
            }
        }
        
        // Preserve any parent siblings that are currently selected
        const selectedParentSiblings = parentItemSiblings.filter(siblingValue => 
            currentValues.includes(siblingValue)
        );
        
        // Also preserve direct siblings of the clicked child that are currently selected
        const directSiblings = getExistingSiblingValues(parentItem, childFilter.value, splitNestedFacet.data);
        const selectedDirectSiblings = directSiblings.filter(siblingValue => 
            currentValues.includes(siblingValue)
        );
        
        // Use helper function to preserve sibling branch values
        const siblingBranchValues = preserveSiblingBranchesAtAllLevels(
            { value: childFilter.value }, 
            mappedData, 
            currentValues
        );
        
        let updatedValues = [...valuesOutsideBranch, ...selectedParentSiblings, ...selectedDirectSiblings, ...siblingBranchValues];

        // Check if parent is currently selected OR if all siblings are selected (parent was previously expanded)
        const isParentSelected = currentValues.includes(parentItem.value);
        const allSiblings = getExistingSiblingValues(parentItem, '', splitNestedFacet.data);
        const areAllSiblingsCurrentlySelected = allSiblings.every(siblingValue => 
            currentValues.includes(siblingValue)
        );
        
        if (isParentSelected || areAllSiblingsCurrentlySelected) {
            // Parent is selected OR all siblings are selected, so clicking a child means "deselect this child"
            // Remove parent (if it exists) and add all siblings EXCEPT the clicked child
            if (isParentSelected) {
                updatedValues = updatedValues.filter(value => value !== parentItem.value);
            }
            const siblingsExceptClicked = allSiblings.filter(siblingValue => siblingValue !== childFilter.value);
            
            // case: if parent has only 1 child and that child is being "deselected",
            // don't add any siblings (since there are no other siblings to select)
            if (allSiblings.length === 1 && allSiblings[0] === childFilter.value) {
                // Don't add any siblings - this effectively deselects the only child
            } else {
                updatedValues.push(...siblingsExceptClicked);
            }
        } else {
            // Normal individual child selection logic
            if (childFilter.selected) {
                // Toggling child OFF (already handled by filtering out branch values)
            } else {
                // Toggling child ON
                updatedValues.push(childFilter.value);
                
                // Check if all siblings are now selected - if so, consolidate to parent
                if (areAllSiblingsSelected(parentItem, childFilter, updatedValues, splitNestedFacet.data)) {
                    // Remove all children, add parent instead
                    const childValues = getExistingChildValues(parentItem, splitNestedFacet.data);
                    updatedValues = updatedValues.filter(v => !childValues.includes(v));
                    updatedValues.push(parentItem.value);
                }
            }
        }

        const allFilters = [...otherFiltersInQuery];
        if (updatedValues.length > 0) {
            allFilters.push({ field: splitNestedFacet.field, value: updatedValues });
        }
        updateSearch({ products: { filter: allFilters } });
    }, [curFilters, splitNestedFacet, mappedData, updateSearch]);

    const newParentFilters = useMemo(() => [filter, ...parentFilters], [filter, parentFilters]);

    return (
        <li className="ns-filter ns-flex ns-flex-col">
            <div className="ns-flex ns-items-center ns-justify-between ns-flex-nowrap">
                <Checkbox 
                    valueName={item.value.replace("Category: ", "")}
                    value={item.value}
                    selected={newParentFilters.some(parentFilter => parentFilter?.selected)}
                    id={`ns-${splitNestedFacet.id}-${item.value.toString().toLowerCase().replace(/\s+/g, '-')}`}
                    onChange={handleParentFilterChange}
                    className=""
                />
                <button
                    type="button"
                    className="ns-nested-categories-toggle"
                    aria-label={`${open ? 'Collapse' : 'Expand' } ${item.value.replace("Category: ", "")} subcategories`}
                    aria-expanded={open}
                    aria-controls={`ns-${splitNestedFacet.id}-${item.value.toString().toLowerCase().replace(/\s+/g, '-')}-submenu`}
                    onClick={e => {
                        e.preventDefault();
                        setOpen(!open);
                    }}
                >
                    <span 
                        aria-hidden="true" 
                        role="presentation" 
                        className={`ns-plus-minus-icon ns-block ns-relative ${open ? 'ns-rotated' : ''}`}
                    />
                </button>
            </div>
            <div
                className="ns-facet-body"
                id={`ns-${splitNestedFacet.id}-${item.value.toString().toLowerCase().replace(/\s+/g, '-')}-submenu`}
                aria-labelledby={`ns-${splitNestedFacet.id}-${item.value.toString().toLowerCase().replace(/\s+/g, '-')}`}
                aria-hidden={!open}
                // style={`--dropdown-height: ${dropdownHeight}px;`}
                // ref={dropdownRef}
            >
                <ul 
                    className="ns-sub-filter-list ns-list-unstyled ns-flex ns-flex-col ns-ml-4" 
                    role="menu"
                >
                {item.children.map(child => {
                    const childFilter = splitNestedFacet.data.find(dataItem => dataItem.value === child.value);

                    const childIsSelected = () => {
                        const hasSelectedParent = newParentFilters.some(parentFilter => parentFilter?.selected);
                        return hasSelectedParent || childFilter.selected;
                    };
                    return (childFilter && child.children ? (
                        <CheckboxWithChildren 
                            key={child.value}
                            item={child}  
                            filter={childFilter}
                            splitNestedFacet={splitNestedFacet}
                            parentFilters={newParentFilters}
                            mappedData={mappedData}
                        />
                    ) : childFilter ? (
                        <Checkbox
                            key={child.value}
                            className=""
                            valueName={facetConfigs[splitNestedFacet.id]?.nameMap?.[child.value] || child.value}
                            value={childFilter.value}
                            selected={childIsSelected()}
                            id={`ns-${splitNestedFacet.id}-${childFilter.value.toString().toLowerCase().replace(/\s+/g, '-')}`}
                            onChange={e => {
                                e.preventDefault()
                                handleChildFilterChange(childFilter, item)
                            }}
                        />
                    ) : null);
                })}
                </ul>
            </div>
        </li>
    )
}